// pages/components/billing/PlanComparison.jsx - UPDATED VERSION
import React, { useState, useEffect } from 'react';
import { Check, Sparkles, Zap, Crown, ArrowRight, Loader2, AlertCircle, CheckCircle, X } from 'lucide-react';
import apiRequest from '@/services/requests';

const PlanComparison = ({ currentPackage, onUpgrade, handleUpgrade, upgrading, packages, loading }) => {


if (loading || !packages) {
return (
<div className="flex items-center justify-center py-12">
<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
</div>
);
}

const packageOrder = ['standard', 'premium', 'elite'];
const packageIcons = {
standard: Sparkles,
premium: Zap,
elite: Crown
};

const packageColors = {
standard: {
gradient: 'from-blue-100 to-blue-200',
button: 'bg-blue-600 hover:bg-blue-700',
border: 'border-blue-300',
badge: 'bg-blue-100 text-blue-700'
},
premium: {
gradient: 'from-purple-100 to-purple-200',
button: 'bg-purple-600 hover:bg-purple-700',
border: 'border-purple-300',
badge: 'bg-purple-100 text-purple-700',
popular: true
},
elite: {
gradient: 'from-amber-100 to-amber-200',
button: 'bg-amber-600 hover:bg-amber-700',
border: 'border-amber-300',
badge: 'bg-amber-100 text-amber-700'
}
};

return (
<>
<div className="space-y-8">
<div className="text-center">
<h2 className="text-xl lg:text-2xl font-bold text-slate-900">Upgrade or change plan</h2>
<p className="text-slate-600 text-sm mt-1">
Select the perfect plan for your job search journey
</p>
</div>

{/* Info Banner for Mid-Cycle Changes */}
<div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
<div className="flex items-start gap-3">
<AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
<div className="flex-1">
<p className="text-sm text-blue-900 font-medium">
💡 Mid-cycle package changes
</p>
<p className="text-xs text-blue-700 mt-1">
<strong>Upgrades:</strong> You'll be charged the pro-rated difference. Your unused credit is applied automatically.<br/>
<strong>Downgrades:</strong> Changes take effect immediately. Full payment for the new package cycle.
</p>
</div>
</div>
</div>

<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
{packageOrder.map((packageKey) => {
const pkg = packages[packageKey];
const colors = packageColors[packageKey];
const Icon = packageIcons[packageKey];
const isCurrent = packageKey === currentPackage;
const isUpgrading = upgrading === packageKey;

return (
<div
key={packageKey}
className={`relative bg-white rounded-2xl border-2 ${colors.border} shadow-lg hover:shadow-xl transition-all ${
colors.popular ? 'ring-2 ring-purple-400 ring-offset-2' : ''
}`}
>
{/* Popular Badge */}
{colors.popular && (
<div className="absolute -top-4 left-1/2 -translate-x-1/2">
<div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
⭐ Most Popular
</div>
</div>
)}

{/* Current Plan Badge */}
{isCurrent && (
<div className="absolute -top-3 right-4">
<div className={`${colors.badge} px-3 py-1 rounded-full text-xs font-bold shadow-md`}>
Current Plan
</div>
</div>
)}

{/* Header */}
<div className={`bg-gradient-to-br ${colors.gradient} p-6 rounded-t-2xl`}>
<div className="flex items-center gap-2 mb-2">
{Icon && <Icon size={24} className="text-slate-700" />}
<h3 className="text-2xl font-bold text-slate-900">{pkg.name}</h3>
</div>
<div className="flex items-baseline gap-1">
<span className="text-4xl font-bold text-slate-900">
${pkg.price}
</span>
<span className="text-slate-600">
{pkg.is_quarterly ? '/quarter' : '/month'}
</span>
</div>
{pkg.is_quarterly && (
<p className="text-xs text-slate-600 mt-1">
(${pkg.monthly_price}/month billed quarterly)
</p>
)}
{pkg.jobs_limit !== -1 ? (
<p className="text-sm text-slate-600 mt-2">
Up to {pkg.jobs_limit} job matches/month
</p>
) : (
<p className="text-sm text-slate-600 mt-2">
Unlimited job matches
</p>
)}
</div>

{/* Features */}
<div className="p-6 space-y-3">
{pkg.features.map((feature, idx) => (
<div key={idx} className="flex items-start gap-3">
<div className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${
feature.included ? 'bg-green-100' : 'bg-slate-100'
}`}>
{feature.included && (
<Check size={14} className="text-green-600 font-bold" />
)}
</div>
<span className={`text-sm ${
feature.included ? 'text-slate-700' : 'text-slate-400'
}`}>
{feature.name}
</span>
</div>
))}
</div>

{/* Action Button */}
<div className="p-6 pt-0">
{isCurrent ? (
<button
disabled
className="w-full py-3 bg-slate-200 text-slate-500 font-semibold rounded-lg cursor-not-allowed"
>
Current Plan
</button>
) : (
<button
onClick={() => handleUpgrade(packageKey)}
disabled={isUpgrading}
className={`w-full py-3 ${colors.button} text-white font-semibold rounded-lg shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2`}
>
{isUpgrading ? (
<>
<Loader2 className="animate-spin" size={18} />
Processing...
</>
) : (
<>
{pkg.price > packages[currentPackage]?.price ? 'Upgrade' : 'Downgrade'} Now
<ArrowRight size={18} />
</>
)}
</button>
)}
</div>
</div>
);
})}
</div>

{/* FAQ Section */}
<div className="mt-12 bg-white rounded-2xl border border-slate-200 p-8">
<h3 className="text-2xl font-bold text-slate-900 mb-6">Frequently Asked Questions</h3>
<div className="space-y-6">
<div>
<h4 className="font-semibold text-slate-900 mb-2">Can I change plans anytime?</h4>
<p className="text-slate-600 text-sm">
Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately after payment.
</p>
</div>
<div>
<h4 className="font-semibold text-slate-900 mb-2">What happens to my unused time when upgrading?</h4>
<p className="text-slate-600 text-sm">
When upgrading, we calculate your unused credit from the current package and apply it to the new package cost. You only pay the pro-rated difference!
</p>
</div>
<div>
<h4 className="font-semibold text-slate-900 mb-2">What happens to my job match quota when changing plans?</h4>
<p className="text-slate-600 text-sm">
<strong>Upgrading:</strong> You immediately get the full new monthly quota.<br/>
<strong>Downgrading:</strong> Your remaining matches are capped at the new package limit.
</p>
</div>
<div>
<h4 className="font-semibold text-slate-900 mb-2">Do you offer refunds?</h4>
<p className="text-slate-600 text-sm">
We offer a 7-day money-back guarantee on all paid plans. If you're not satisfied, 
contact our support team for a full refund.
</p>
</div>
</div>
</div>
</div>


</>
);
};

export default PlanComparison;